import {
  Cloud,
  FileText,
  Heart,
  Link2,
  Pencil,
  Server,
  User,
} from "lucide-react";
import { useContext, useMemo, useState } from "react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "components/ui/dropdown-menu";
import { Button } from "components/ui/button";
import {
  JOB_APPLICATION_MATCH_STATUS,
  JOB_APPLICATION_MATCH_SUB_STATUS,
  JOB_OPPORTUNITY_STATUSES,
} from "lookup";
import {
  AuthContext,
  JobsContext,
  MatchContext,
  ModalContext,
  NotesContext,
} from "context/providers";
import SvgIcon from "components/base/SvgIcon";
import { SVG_ICON_SURFACE_CLASS } from "components/base/SvgButton";
import { getButtonText } from "components/UserCardList/helpers/userCard";
import { getPlatformProfileUrl, getUserName } from "utils/helpers/users";
import { PerfectFitJobModal } from "containers/ModalContainer/modals";
import UserInfoModal from "containers/ModalContainer/modals/UserInfoModal";
import { cn } from "utils";
import { CTA_TITLES } from "components/UserCardList/constants/ctaTitles";
import { useConfirmationEmailNotification } from "components/UserCardList/hooks/useConfirmationEmailNotification";
import FullProfileDialog from "../shared/FullProfileDialog";
import { isMatchPreShortlistedStatusType } from "utils/helpers/match";

const PRIMARY_ACTION_CLASS =
  "inline-flex w-fit items-center gap-2 whitespace-nowrap rounded-md px-2 py-1.5 text-sm font-semibold text-gray-700 transition-colors hover:bg-accent hover:text-accent-foreground disabled:pointer-events-none disabled:opacity-50";
const ICON_ONLY_PRIMARY_ACTION_CLASS =
  "inline-flex h-8 w-8 items-center justify-center rounded-md text-sm font-semibold text-gray-700 transition-colors hover:bg-accent hover:text-accent-foreground disabled:pointer-events-none disabled:opacity-50";

function HitActionDropDown({
  row,
  collectionKey,
  activeTabName,
  showPrimaryActions = false,
  hideOverflow = false,
  hidePrimaryActionsInMenu = false,
  hideMatchActions = false,
  showFullProfileMenuItem = false,
}) {
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [perfectFitJobModalIsOpen, setPerfectFitJobModalIsOpen] =
    useState(false);
  const [matchInfoModalIsOpen, setMatchInfoModalIsOpen] = useState(false);
  const [isFullProfileDialogOpen, setIsFullProfileDialogOpen] = useState(false);

  const { user } = useContext(AuthContext);
  const { jobOpp, convertJobLongListToMatch } = useContext(JobsContext);
  const { updateMatch, createMatch } = useContext(MatchContext);
  const { showModal } = useContext(ModalContext);
  const { userNotes, user: initUserNotes } = useContext(NotesContext);

  const hit = row.original;
  const notesCount = userNotes[hit.id]?.length ?? hit.notesCount ?? 0;
  const {
    confirmationEmailLabel,
    isSendingConfirmationEmail,
    sendUserNotification,
  } = useConfirmationEmailNotification(hit.email);
  const matchStatus = hit.associatedMatch?.status;

  const canBeMatched =
    matchStatus &&
    (isMatchPreShortlistedStatusType(matchStatus) ||
      matchStatus === JOB_APPLICATION_MATCH_STATUS.SHORTLISTED ||
      matchStatus === JOB_APPLICATION_MATCH_STATUS.INTERESTED);

  const canBeSkipped =
    matchStatus &&
    (isMatchPreShortlistedStatusType(matchStatus) ||
      matchStatus === JOB_APPLICATION_MATCH_STATUS.SHORTLISTED);

  const hasPrimaryMatcher = !!jobOpp.torcOwner?.username;
  const isJobStatusActive = jobOpp.status === JOB_OPPORTUNITY_STATUSES.ACTIVE;
  const isAdmin = useMemo(
    () =>
      user?.attributes?.groups?.includes(
        process.env.REACT_APP_COGNITO_ADMIN_GROUP
      ),
    [user]
  );

  const [isLoading, setIsLoading] = useState({
    [JOB_APPLICATION_MATCH_STATUS.MATCHED]: false,
    [JOB_APPLICATION_MATCH_STATUS.SHORTLISTED]: false,
    [JOB_APPLICATION_MATCH_STATUS.SKIPPED]: false,
    [JOB_APPLICATION_MATCH_STATUS.DELETED]: false,
    [JOB_APPLICATION_MATCH_SUB_STATUS.FINALIST]: false,
    [JOB_APPLICATION_MATCH_SUB_STATUS.NULL]: false,
    [JOB_APPLICATION_MATCH_STATUS.REJECTEDBYMATCHER]: false,
    [JOB_APPLICATION_MATCH_STATUS.APPLIED]: false,
  });

  const oneOfButtonsLoading = useMemo(() => {
    return !!Object.values(isLoading).filter((el) => !!el).length;
  }, [isLoading]);

  const { applicationId, jobOpportunityId } = useMemo(() => {
    const app = Array.isArray(hit.applications)
      ? hit.applications.find((a) => a.jobTypeId === jobOpp.jobType?.id)
      : undefined;

    return {
      applicationId:
        hit?.match?.applicationId ||
        hit?.associatedMatch?.applicationId ||
        app?.id ||
        `User#${hit.id}`,
      jobOpportunityId: jobOpp?.id,
    };
  }, [hit, jobOpp]);

  const matchAction = async (args, isUpdate) => {
    if (args.status === JOB_APPLICATION_MATCH_STATUS.MATCHED) {
      showModal({
        type: "match",
        applicationId,
        jobOpportunityId,
        userId: hit.id,
        userIsUnclaimed: hit.isUnclaimed,
        collectionKey,
        status: args.status,
        isUpdate,
        isJobLongList: hit.match?.canConvertToMatch,
        resumeLocation: hit.match?.resumeLocation,
        hitStatus: hit.match?.status || hit.associatedMatch?.status,
      });
      return;
    }

    setIsLoading((prev) => ({ ...prev, [args.status]: true }));

    if (hit.isUnclaimed) {
      args.unclaimedWhenMatched = true;
    }

    const matchAttrs = {
      ...args,
      ...(hit.match?.canConvertToMatch && hit.match?.resumeLocation
        ? { resumeLocation: hit.match.resumeLocation }
        : {}),
    };

    try {
      if (isUpdate) {
        await updateMatch(applicationId, jobOpportunityId, matchAttrs);
      } else {
        await createMatch(jobOpportunityId, hit.id, matchAttrs);
        if (hit.match?.canConvertToMatch) {
          await convertJobLongListToMatch(jobOpportunityId, hit.id);
        }
      }
    } catch (error) {
      console.log("matchAction error: ", error);
    } finally {
      setIsLoading((prev) => ({ ...prev, [args.status]: false }));
    }
  };

  const handleMatch = () => {
    if (!hit.hasActivePlacements) {
      matchAction(
        { status: JOB_APPLICATION_MATCH_STATUS.MATCHED },
        !!matchStatus
      );
      return;
    }

    if (
      window.confirm(
        "Are you sure you want to match this user, even though they have active engagement(s) currently?"
      )
    ) {
      matchAction(
        { status: JOB_APPLICATION_MATCH_STATUS.MATCHED },
        !!matchStatus
      );
    }
  };

  const matchDisabled =
    oneOfButtonsLoading || !isJobStatusActive || !hasPrimaryMatcher;

  const matchTitle = !isJobStatusActive
    ? "This job is not active"
    : !hasPrimaryMatcher
    ? "This job has no primary matcher"
    : hit.hasActivePlacements
    ? "WARNING: User has active engagement(s)"
    : "";
  const matchButtonTitle = matchTitle
    ? `${CTA_TITLES.match} ${matchTitle}`
    : CTA_TITLES.match;

  const handleMoreInfoSelect = (e) => {
    e.preventDefault();
    setIsDropdownOpen(false);
    setMatchInfoModalIsOpen(true);
  };

  const handlePerfectFitJobsSelect = (e) => {
    e.preventDefault();
    setIsDropdownOpen(false);
    setPerfectFitJobModalIsOpen(true);
  };

  const primaryActions = (
    <>
      {(!hit.associatedMatch || canBeMatched) && (
        <button
          className={PRIMARY_ACTION_CLASS}
          disabled={matchDisabled}
          onClick={handleMatch}
          title={matchButtonTitle}
        >
          {hit.hasActivePlacements ? (
            <SvgIcon type="warning" className="w-5 h-5" />
          ) : (
            <Heart size={20} />
          )}
        </button>
      )}

      {!hit.associatedMatch ? (
        <button
          className={cn(
            showPrimaryActions
              ? ICON_ONLY_PRIMARY_ACTION_CLASS
              : PRIMARY_ACTION_CLASS
          )}
          disabled={oneOfButtonsLoading}
          onClick={() =>
            matchAction(
              { status: JOB_APPLICATION_MATCH_STATUS.SHORTLISTED },
              !!matchStatus
            )
          }
          title={CTA_TITLES.shortlist}
          aria-label={CTA_TITLES.shortlist}
        >
          <SvgIcon type="focus" className="w-5 h-5" />
          {!showPrimaryActions ? getButtonText("shortlist") : null}
        </button>
      ) : (
        <span
          aria-hidden="true"
          className={cn(
            showPrimaryActions
              ? ICON_ONLY_PRIMARY_ACTION_CLASS
              : PRIMARY_ACTION_CLASS,
            "invisible pointer-events-none"
          )}
        >
          <SvgIcon type="focus" className="w-5 h-5" />
          {!showPrimaryActions ? getButtonText("shortlist") : null}
        </span>
      )}

      {(!hit.associatedMatch || canBeSkipped) && (
        <button
          className={cn(
            showPrimaryActions
              ? ICON_ONLY_PRIMARY_ACTION_CLASS
              : PRIMARY_ACTION_CLASS
          )}
          disabled={oneOfButtonsLoading}
          onClick={() =>
            matchAction(
              { status: JOB_APPLICATION_MATCH_STATUS.SKIPPED },
              !!matchStatus
            )
          }
          title={CTA_TITLES.skip}
          aria-label={CTA_TITLES.skip}
        >
          <SvgIcon type="skip" className="w-5 h-5" />
          {!showPrimaryActions ? getButtonText("skip") : null}
        </button>
      )}
    </>
  );

  return (
    <div className="flex items-center gap-2">
      {showPrimaryActions && !hideMatchActions ? primaryActions : null}

      {!hideOverflow ? (
        <DropdownMenu
          modal={false}
          open={isDropdownOpen}
          onOpenChange={(open) => {
            setIsDropdownOpen(open);
            if (open && hit.id && !userNotes[hit.id]) {
              initUserNotes(hit);
            }
          }}
        >
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" className="h-8 w-8 p-0">
              <span className="sr-only">Open action menu</span>
              <SvgIcon type="moreVertical" className={SVG_ICON_SURFACE_CLASS} />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            {!showPrimaryActions &&
            !hidePrimaryActionsInMenu &&
            !hideMatchActions
              ? primaryActions
              : null}

            <DropdownMenuItem
              title={CTA_TITLES.userNotes}
              onClick={() =>
                showModal({
                  type: "note",
                  title: `User notes: ${getUserName(hit)}`,
                  userId: hit.id,
                })
              }
            >
              <FileText size={20} />
              User Notes{!!notesCount ? ` (${notesCount})` : ""}
            </DropdownMenuItem>

            <DropdownMenuSeparator />

            <DropdownMenuItem
              title={CTA_TITLES.editProfile}
              onClick={() =>
                showModal({ type: "userEditProfile", userId: hit.id })
              }
            >
              <Pencil size={16} />
              Edit Profile
            </DropdownMenuItem>

            {user?.canSendAssessments ? (
              <DropdownMenuItem
                title={CTA_TITLES.assessments}
                onClick={() =>
                  showModal({ type: "assessment", userId: hit.id })
                }
              >
                <SvgIcon type="openBook" className="w-4 h-4" />
                Assessments
              </DropdownMenuItem>
            ) : null}

            {hit.isUnclaimed ? (
              <DropdownMenuItem
                title={CTA_TITLES.sendConfirmationEmail}
                disabled={isSendingConfirmationEmail}
                onClick={sendUserNotification}
              >
                <SvgIcon type="simpleEmail" className="w-4 h-4" />
                {confirmationEmailLabel}
              </DropdownMenuItem>
            ) : null}

            <DropdownMenuItem
              title={CTA_TITLES.findPerfectFitJobs}
              onSelect={handlePerfectFitJobsSelect}
            >
              <SvgIcon type="compass" className="w-4 h-4" />
              Find Perfect Fit Jobs
            </DropdownMenuItem>

            {showFullProfileMenuItem ? (
              <DropdownMenuItem
                data-action="view-full-profile"
                title={CTA_TITLES.viewFullProfile}
                onSelect={(event) => {
                  event.preventDefault();
                  setIsDropdownOpen(false);
                  setIsFullProfileDialogOpen(true);
                }}
              >
                <User size={16} />
                Profile
              </DropdownMenuItem>
            ) : null}

            <DropdownMenuItem
              title={CTA_TITLES.addLink}
              onClick={() =>
                showModal({
                  type: "link",
                  userId: hit.id,
                  applicationId: hit?.match?.applicationId,
                  collectionKey,
                })
              }
            >
              <Link2 size={16} />
              Add Link
            </DropdownMenuItem>

            <DropdownMenuItem
              title={CTA_TITLES.moreInfo}
              onSelect={handleMoreInfoSelect}
            >
              <SvgIcon type="info" className="w-4 h-4" />
              More Info
            </DropdownMenuItem>

            <DropdownMenuItem asChild>
              <a
                href={getPlatformProfileUrl(getUserName(hit))}
                target="_blank"
                rel="noreferrer"
                className="w-full"
                title={CTA_TITLES.openPlatformProfile}
              >
                <User size={16} />
                Open Platform Profile
              </a>
            </DropdownMenuItem>

            <DropdownMenuSeparator />

            {isAdmin && getUserName(hit) ? (
              <DropdownMenuItem asChild>
                <a
                  href={`https://admin.torc.dev/users/${getUserName(hit)}`}
                  target="_blank"
                  rel="noreferrer"
                  className="w-full"
                  title={CTA_TITLES.adminTool}
                >
                  <Server size={16} />
                  Admin Tool
                </a>
              </DropdownMenuItem>
            ) : null}

            <DropdownMenuItem asChild>
              <a
                href={`${process.env.REACT_APP_SFDC_BASE_URL}/torcUserRedirect?id=${hit.id}`}
                target="_blank"
                rel="noreferrer"
                className="w-full"
                title={CTA_TITLES.sfdcUser}
              >
                <Cloud size={16} />
                SFDC User
              </a>
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      ) : null}

      <PerfectFitJobModal
        isDialogOpen={perfectFitJobModalIsOpen}
        setDialogVisibility={setPerfectFitJobModalIsOpen}
        user={hit}
      />

      {showFullProfileMenuItem ? (
        <FullProfileDialog
          open={isFullProfileDialogOpen}
          onOpenChange={setIsFullProfileDialogOpen}
          hit={hit}
          activeTabName={activeTabName}
          collectionKey={collectionKey}
        />
      ) : null}

      <UserInfoModal
        open={matchInfoModalIsOpen}
        onOpenChange={setMatchInfoModalIsOpen}
        match={hit.match || {}}
        user={hit}
      />
    </div>
  );
}

export default HitActionDropDown;
